import {FulltextSearch} from '@/api/meta/filters';
import {autoImplement} from '@/util';
import moment, {Moment} from 'moment';
import Position, {ApiPosition} from './Position';
import Tour, {ApiTour} from './Tour';

export enum ApiPicklistState {
  Todo = 1,
  Active = 2,
  Done = 3,
  Parked = 4,
}

export enum ApiPicklistType {
  Tour = 'TR',
  Order = 'VA',
  Internal = 'LL',
  Incoming = 'EA',
  Inventory = 'IV',
  Suggestion = 'SG',
  Correction = 'CR',
  Umbuchung = 'UB',
}

export interface ApiPicklist {
  Art: ApiPicklistType;
  Picklistnr: number | string;
  Erstelltam: string;
  Erstelltdurch: string;
  Status: ApiPicklistState;
  Kmsdatum: string;
  Kmsuhrzeit: string;
  Kmsprioritaet: string;
  Kmsam: string;
  Kmsdurch: string;
  Kmsbediener: string | null;
  Tour: ApiTour[];
  Auftraege: ApiPosition[];
  InterneAuftraege: ApiPosition[];
}

export interface PicklistFilters {
  Status$In?: ApiPicklistState[];
  Kmsdatum$From?: string;
  Kmsdatum$To?: string;
  Art$In?: ApiPicklistType[];
  Fulltext$Fulltext: FulltextSearch;
  Kmsbediener?: string;
}

export interface DetailPicklistFilters {
  Picklistnr?: string;
}

interface PicklistIncomingFields {
  LieferantenNr: number | null;
  KundenNr: number | null;
  BestellNr: number | null;
}

interface PicklistIncomingInterface extends ApiPicklist, PicklistIncomingFields {
}

interface PicklistUmbuchungFields {
  NiederlassungEingehend: {
    Niederlassung: number | null;
    Name1: string | null;
  }
  NiederlassungAbgehend: {
    Niederlassung: number | null;
    Name1: string | null;
  }
}

interface PicklistUmbuchungInterface extends ApiPicklist, PicklistUmbuchungFields {
}

interface PicklistInterface extends Partial<PicklistUmbuchungFields>, Partial<PicklistIncomingFields>, Omit<ApiPicklist, 'Auftraege' | 'InterneAuftraege'> {
  allPositions: Position[];
  Tour: Tour[];
  ErstelltamParsed: Moment;
  KmsdatumParsed: Moment;
  KmsamParsed: Moment;
  totalItemCount: number;
  lieferantName?: string;
  maxPosNr: number | null;
  inWork?: boolean;
  done?: boolean;
}

export default class Picklist extends autoImplement<PicklistInterface>() {
  static fromApi(apiPicklist: ApiPicklist | PicklistIncomingInterface | PicklistUmbuchungInterface, collectData: boolean = true): Picklist {
    let totalItemCount = 0;

    const {
      Auftraege: AuftraegeRaw,
      InterneAuftraege: InterneAuftraegeRaw,
      ...data
    } = apiPicklist;

    let maxPosNr: number | null = null;

    const allPositions: Position[] = [];

    function collectAuftraege(p: ApiPosition) {
      const position = Position.fromApi(p);
      if (maxPosNr === null || position.Posnr > maxPosNr) maxPosNr = position.Posnr;
      allPositions.push(position);
      totalItemCount += position.Gebindemenge ?? position.Menge;
    }

    if (collectData) {
      AuftraegeRaw.forEach(collectAuftraege);
      InterneAuftraegeRaw.forEach(collectAuftraege);
    }

    let tour: Tour[] = [];

    if (apiPicklist.Tour != undefined) {
      tour = apiPicklist.Tour.map((t) => Tour.fromApi(t));
    }

    return new Picklist({
      ...data,
      allPositions,
      ErstelltamParsed: moment(apiPicklist.Erstelltam),
      KmsdatumParsed: moment(apiPicklist.Kmsdatum),
      KmsamParsed: moment(apiPicklist.Kmsam),
      totalItemCount,
      Tour: tour,
      maxPosNr,
    });
  }

  get KmsdatumDate() {
    return this.KmsdatumParsed.format('DD.MM.');
  }

  get ErstelltamDate() {
    return this.ErstelltamParsed.format('DD.MM.YYYY');
  }

  get ErstelltamShortDate() {
    return this.ErstelltamParsed.format('DD.MM.');
  }

  get ErstelltamTime() {
    return this.ErstelltamParsed.format('H:mm');
  }

  get DatumStatus() {
    const dateDiff = moment().diff(this.KmsdatumParsed, 'days');
    return dateDiff > 0 ? 'passed' : 'active';
  }

  get StatusCode() {
    switch (this.Status) {
      case ApiPicklistState.Todo:
        return 'todo';
      case ApiPicklistState.Active:
        return 'active';
      case ApiPicklistState.Parked:
        return 'parked';
      case ApiPicklistState.Done:
        return 'done';
    }
    return '';
  }

  get StatusColor() {
    switch (this.Status) {
      case ApiPicklistState.Todo:
        return 'primary';
      case ApiPicklistState.Active:
        return 'primary-lighten-1';
      case ApiPicklistState.Parked:
        return 'orange-accent-4';
      case ApiPicklistState.Done:
        return 'success';
    }

    return '';
  }

  get StatusName() {
    switch (this.Status) {
      case ApiPicklistState.Todo:
        return 'offen';
      case ApiPicklistState.Active:
        return 'Bearbeitung';
      case ApiPicklistState.Parked:
        return 'geparkt';
      case ApiPicklistState.Done:
        return 'erledigt';
    }

    return '';
  }

  get StatusIcon() {
    switch (this.Status) {
      case ApiPicklistState.Todo:
        return '';
      case ApiPicklistState.Active:
        return 'lock';
      case ApiPicklistState.Parked:
        return 'pause-circle';
      case ApiPicklistState.Done:
        return 'check-circle';
    }

    return '';
  }


  // Typ
  get isIncoming() {
    return this.Art === ApiPicklistType.Incoming;
  }

  get isOutgoing() {
    return !this.isIncoming && !this.isInventory && !this.isSuggestion && !this.isUmbuchung && !this.isCorrection;
  }

  get allowOvershoot() {
    return !this.isOutgoing || this.Picklistnr === '-1';
  }

  get isInventory() {
    return this.Art === ApiPicklistType.Inventory;
  }

  get isSuggestion() {
    return this.Art === ApiPicklistType.Suggestion;
  }

  get isCorrection() {
    return this.Art === ApiPicklistType.Correction;
  }

  get isUmbuchung() {
    return this.Art === ApiPicklistType.Umbuchung;
  }

  get mayAddBooking() {
    return !this.isInventory && !this.isSuggestion && !this.isUmbuchung && !this.isCorrection;
  }

  get mayAddPosition() {
    return !this.isOutgoing || this.Picklistnr === '-1';
  }

  get showProgress() {
    return !this.isIncoming && !this.isInventory;
  }

  get showBelegnr() {
    return !this.isInventory && !this.isSuggestion;
  }

  get saveBtnText() {
    if (this.isInventory || this.isSuggestion || this.isUmbuchung || this.isCorrection) return 'Speichern';
    return 'Buchen';
  }
}
